import { Module } from 'vuex'
import config from 'config'
import RootState from '@vue-storefront/core/types/RootState'
import { processLocalizedURLAddress } from '@vue-storefront/core/helpers'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import { Logger } from '@vue-storefront/core/lib/logger'
import { clearVariant, isMappedVariant, readVariant, writeVariant } from '../helpers'

const RETRY_DELAY_MS = 300

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

export const module: Module<any, RootState> = {
  namespaced: true,
  state: {
    variant: null,
    // Distinguishes "assignment not asked yet" from "asked, and there is no
    // variant" - only the former may leave the checkout CTA disabled.
    resolved: false,
    // The last cart token the server confirmed a stamp for, so repeat forces
    // for the same quote are skipped.
    stampedToken: null
  },
  getters: {
    getVariant: state => state.variant,
    isResolved: state => state.resolved
  },
  mutations: {
    SET_VARIANT (state, variant) {
      state.variant = variant
    },
    SET_RESOLVED (state, resolved) {
      state.resolved = resolved
    },
    SET_STAMPED_TOKEN (state, cartToken) {
      state.stampedToken = cartToken
    }
  },
  actions: {
    /**
     * Confirms or assigns the variant and stamps it on the quote, committing it
     * so components react instead of snapshotting it once at mount. A held
     * variant is never discarded because the request failed.
     *
     * `force` is what callers that must WRITE the stamp pass; without it a held
     * variant is reused, so an ordinary page load costs no round trip.
     */
    async syncVariant (context, { cartToken = '', force = false } = {}) {
      const routes = config.abTest?.checkout || {}
      const stored = readVariant()
      // An unmapped letter is treated as no assignment, so the customer is never
      // routed to one variant while being reported under another.
      const held = isMappedVariant(stored, routes) ? stored : null

      if (stored && !held) {
        Logger.info(`Releasing held variant "${stored}", it has no configured checkout route`, 'ab-test')()
        clearVariant()
      }

      const endpoint = config.abTest?.variantEndpoint
      // A quote already stamped this session needs no second write - the
      // server's write-once guard would make it a no-op anyway.
      const write = force && !!cartToken && context.state?.stampedToken !== cartToken

      // Nothing held and no cart to stamp means the visitor has not been exposed
      // to a checkout yet, so there is nothing to confirm and nothing to write
      // to. Asking anyway costs a round trip that repeats on every page load for
      // as long as no cookie comes back - which is exactly the state the
      // experiment ships in.
      //
      // It returns BEFORE the try on purpose: the `finally` marks assignment
      // concluded, and this call concluded nothing. Marking it here would leave
      // `resolved` true for the rest of the page, so the CTA rendered once a cart
      // token exists would never count as pending and would show the default
      // route while the real letter is still in flight.
      if (!held && !cartToken) {
        return null
      }

      try {
        if (!endpoint || (held && !write)) {
          if (held) {
            context.commit('SET_VARIANT', held)
          }

          return held
        }

        // A signed-in customer's cart token is the numeric quote id, which
        // only the authenticated Magento route accepts, so the API middleware
        // needs the customer token to pick that route. The {{token}}
        // placeholder, not the literal token: the task substitutes it at
        // execute time, which is what lets a 401 retry use a refreshed token
        // and keeps the raw JWT out of the logged task URL.
        const customerToken = context.rootGetters?.['user/getToken']
        const url = customerToken ? `${endpoint}?token={{token}}` : endpoint

        const execute = () => TaskQueue.execute({
          url: processLocalizedURLAddress(url),
          payload: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            mode: 'cors',
            body: JSON.stringify({ variant: held, cartToken: cartToken || null })
          },
          // A failed sync must never surface a raw error toast over the
          // add-to-cart modal; the customer is never blocked by our outage.
          silent: true
        })

        let response

        try {
          response = await execute()
        } catch (firstError) {
          // Retry once, then fall back to the held variant for routing.
          await delay(RETRY_DELAY_MS)
          response = await execute()
        }

        const answered = response?.code === 200
        const variant = answered ? response.result?.variant : null

        // An answer carrying no letter is assignment being switched off, which
        // has to release the customers already holding one. A failed request is
        // not that answer, so an outage still never moves anybody.
        if (answered && !variant) {
          clearVariant()
          context.commit('SET_VARIANT', null)

          return null
        }

        const mapped = isMappedVariant(variant, routes)

        if (variant && !mapped) {
          Logger.error(`Ignoring variant "${variant}" with no configured checkout route`, 'ab-test')()
        }

        if (!mapped) {
          if (held) {
            context.commit('SET_VARIANT', held)
          }

          return held
        }

        writeVariant(variant)
        context.commit('SET_VARIANT', variant)

        if (cartToken) {
          context.commit('SET_STAMPED_TOKEN', cartToken)
        }

        return variant
      } catch (error) {
        Logger.warn('Variant assignment failed, keeping the held variant', 'ab-test', error)()

        if (held) {
          context.commit('SET_VARIANT', held)
        }

        return held
      } finally {
        // Runs on every exit path, present and future, so "assignment
        // concluded" can never be forgotten the way isVariantPending was.
        context.commit('SET_RESOLVED', true)
      }
    }
  }
}
